import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, TextField, Button, Stack, Snackbar } from '@mui/material';
import DialogContainer from '@shared/ui/dialog/DialogContainer';
import DialogHeader from '@shared/ui/dialog/DialogHeader';
import { LastLoginMethod } from '@domain/account/models/LastLoginMethod';
import { container } from '@infrastructure/container';
import { useSignUpStore } from '../../../application/store/SignUpStore';

const SignIn = () => {
  const [email, setEmail] = useState(decodeURIComponent(new URLSearchParams(window.location.search).get('email')?.trim() || ''));
  const [isValid, setIsValid] = useState(false);
  const [isSendLoading, setIsSendLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const navigate = useNavigate();

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEmail = e.target.value;
    setEmail(newEmail);
    setIsValid(validateEmail(newEmail));
  };

  const checkEmailAndContinue = async () => {
    if (!isValid || isSendLoading) return;

    setIsSendLoading(true);
    const response = await container.authRepository.getLastLoginMethod(email);
    setIsSendLoading(false);

    switch (response.status) {  
      case 'success':
        switch (response.data) {
          case LastLoginMethod.OTP:
            const response = await container.authRepository.sendCode(email);
            if (response.status === 'success' && response.data) {
              useSignUpStore.getState().setUserId(response.data.userId);
              useSignUpStore.getState().setEmail(email);
              setErrorMessage('Verification code sent to your email');
              navigate(`/sign-in/validate?email=${encodeURIComponent(email)}`);
            }

            break;
          case LastLoginMethod.EMAIL_PASSWORD:
            navigate(`/sign-in/password?email=${encodeURIComponent(email)}`);
            break;
        }
        break;
      case 'error':
        switch (response.error?.type) {
          case 'UserNotFoundError':
            setErrorMessage('User not found');
            break;
          default:
            setErrorMessage('An unknown error occurred');
            break;
        }
        break;
    }
  };

  return (
    <Box sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      bgcolor: 'background.default',
      p: 0
    }}>
      <DialogContainer>
        <DialogHeader>
          <img height={16} src="assets/images/ic_just_stock.png" alt="logo" />
        </DialogHeader>

        <Box sx={{
          p: 3,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          flex: 1
        }}>
          <Box>
            <Typography variant="body1" fontWeight="medium">Sign In</Typography>
            <Typography variant="body1" color="text.secondary">
              Enter your email to continue
            </Typography>
          </Box>

          <TextField
            autoFocus
            fullWidth
            type="email"
            label="Email"
            value={email}
            onChange={handleEmailChange}
            autoComplete="email"
            size='medium'
            error={email.length > 0 && !isValid}
            helperText={email.length > 0 && !isValid ? "Please enter a valid email" : ""}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && isValid) {
                checkEmailAndContinue();
              }
            }}
          />

          <Box sx={{ flex: 1 }} />

          <Stack
            direction={{ xs: 'column-reverse', sm: 'row' }}
            gap={1}
            sx={{

              justifyContent: 'space-between'
            }}
          >
            <Button
              variant="outlined"
              onClick={() => navigate('/sign-up')}

              size="medium"
            >
              Create Account
            </Button>
            <Button
              variant="contained"
              onClick={checkEmailAndContinue}
              disabled={!isValid || isSendLoading}

              size="medium"
            >
              {isSendLoading ? 'Checking...' : 'Next'}
            </Button>
          </Stack>
        </Box>
      </DialogContainer>

      <Snackbar
        open={!!errorMessage}
        autoHideDuration={6000}
        onClose={() => setErrorMessage(null)}
        message={errorMessage}
      />
    </Box>
  );
};

export default SignIn; 